import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Calendar, Phone, ChevronDown } from 'lucide-react';
import { NAV_LINKS, SERVICE_LINKS, CONTACT } from '../data/mock';
import { Button } from './ui/button';
import { trackCallClick, trackBookButtonClick } from '../lib/analytics';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const handleNavClick = (e, path) => {
    if (path.startsWith('/#')) {
      e.preventDefault();
      const id = path.split('#')[1];
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#f3d6f5]/95 backdrop-blur-md shadow-sm' : 'bg-[#f3d6f5]'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between h-[80px] lg:h-[88px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
          <img
            src={CONTACT.logo}
            alt="Ichi and Ori Pet Care"
            className="h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={(e) => handleNavClick(e, link.path)}
                className={`text-[15px] font-medium transition-colors duration-200 ${
                  active ? 'text-[#e870b6]' : 'text-[#1a0b3d] hover:text-[#e870b6]'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className="text-[15px] font-medium text-[#1a0b3d] hover:text-[#e870b6] inline-flex items-center gap-1">
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full right-0 pt-3 w-64 z-50">
                <div className="bg-white rounded-2xl shadow-xl border border-[#f3d6f5]/60 overflow-hidden">
                  {SERVICE_LINKS.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className="block px-5 py-3 text-[14px] text-[#1a0b3d] hover:bg-[#faf5fb] hover:text-[#e870b6] transition-colors"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            onClick={() => trackCallClick('navbar')}
            className="text-[#1a0b3d] hover:text-[#e870b6] text-[14px] font-semibold inline-flex items-center gap-2"
          >
            <Phone size={14} /> {CONTACT.phone}
          </a>
          <Link to="/book" onClick={() => trackBookButtonClick('navbar')}>
            <Button className="rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-6 py-6 h-auto text-[14px] font-semibold gap-2 shadow-md hover:shadow-lg transition-all">
              <Calendar className="w-4 h-4" />
              Request Appointment
            </Button>
          </Link>
        </div>

        {/* Mobile */}
        <button
          className="lg:hidden text-[#1a0b3d]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#f3d6f5] border-t border-white/40 px-5 py-6 space-y-3 max-h-[calc(100vh-80px)] overflow-y-auto">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={(e) => handleNavClick(e, link.path)}
              className="block text-[#1a0b3d] font-medium text-[16px] hover:text-[#e870b6] py-1"
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-1">
            <p className="text-[12px] uppercase tracking-wider text-[#1a0b3d]/60 mb-2 font-semibold">Services</p>
            {SERVICE_LINKS.map((s) => (
              <Link
                key={s.path}
                to={s.path}
                className="block text-[#1a0b3d] text-[15px] hover:text-[#e870b6] py-1.5 pl-2"
              >
                {s.label}
              </Link>
            ))}
          </div>
          <div className="pt-3 space-y-3">
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('navbar_mobile')} className="flex items-center gap-2 text-[#5b3eb5] font-semibold">
              <Phone size={16} /> {CONTACT.phone}
            </a>
            <Link to="/book" onClick={() => trackBookButtonClick('navbar_mobile')}>
              <Button className="w-full rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white gap-2 mt-1">
                <Calendar className="w-4 h-4" /> Book An Appointment
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
